import React, { useState, useEffect } from "react";
import { updateEpgItem, createSlot } from "../api";

export type MatchedPolicy = {
  id: string;
  name: string;
  action_kind: string;
  priority: number;
  enabled: boolean;
  is_winner: boolean;
};

export type EpgItemDto = {
  id: string;
  title: string;
  starts_at: string;
  ends_at: string;
  status: string;
  audience: string;
  action: string;
  channel: string;
  kind: string;
  tooltip?: string;
  matched_policies?: MatchedPolicy[];
};

type EpgEditorProps = {
  open: boolean;
  item: EpgItemDto | null;
  /** All audience variants of the slot (channel, action, starts_at); includes `item`. */
  siblings?: EpgItemDto[];
  /** "edit" = update an existing mediapoint; "create" = insert a brand-new one. Default "edit". */
  mode?: "edit" | "create";
  onClose: () => void;
  /** Called after a successful save. For Add/Move a toast payload is passed so the parent can offer Undo. */
  onSaved: (toast?: { message: string; opId: string }) => void;
};

// Format an ISO/UTC instant as "YYYY-MM-DDTHH:MM" in the user's LOCAL timezone,
// which is what <input type="datetime-local"> expects.
const toLocal = (iso: string) => {
  const dt = new Date(iso);
  const pad = (n: number) => String(n).padStart(2, "0");
  return (
    `${dt.getFullYear()}-${pad(dt.getMonth() + 1)}-${pad(dt.getDate())}` +
    `T${pad(dt.getHours())}:${pad(dt.getMinutes())}`
  );
};

// Parse a "YYYY-MM-DDTHH:MM" local-time string and return a UTC ISO string.
const toIso = (localStr: string) => new Date(localStr).toISOString();

const EpgEditor: React.FC<EpgEditorProps> = ({ open, item, siblings, mode = "edit", onClose, onSaved }) => {
  const [audience, setAudience] = useState("");
  const [action, setAction] = useState("");
  const [startLocal, setStartLocal] = useState("");
  const [duration, setDuration] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (item) {
      setAudience(item.audience || "");
      setAction(item.action || "");
      setStartLocal(toLocal(item.starts_at));
      setError(null);
      if (item.starts_at && item.ends_at) {
        const start = new Date(item.starts_at);
        const end = new Date(item.ends_at);
        const durationMs = end.getTime() - start.getTime();
        const hours = Math.floor(durationMs / (1000 * 60 * 60));
        const minutes = Math.floor((durationMs % (1000 * 60 * 60)) / (1000 * 60));
        if (hours > 0) {
          setDuration(`PT${hours}H${minutes > 0 ? minutes + "M" : ""}`);
        } else if (minutes > 0) {
          setDuration(`PT${minutes}M`);
        } else {
          setDuration("PT1H");
        }
      }
    }
  }, [item]);

  if (!open || !item) return null;

  const onSave = async () => {
    setSaving(true);
    setError(null);
    try {
      const newMatchTimeIso = startLocal ? toIso(startLocal) : undefined;

      // CREATE mode: channel-scoped slot. The backend derives the channel's
      // audience set (one row per audience), trims overlapping rows, and returns
      // an op_id for undo. No audience field here - the slot spans all audiences.
      if (mode === "create") {
        if (!newMatchTimeIso || !action.trim() || !duration.trim()) {
          throw new Error("Action, start, and duration are required.");
        }
        const res = await createSlot(item.channel, action.trim(), newMatchTimeIso, duration.trim());
        let message = `Added "${action.trim()}" (${res.created} audience${res.created === 1 ? "" : "s"})`;
        if (res.trimmed.length) message += `; trimmed ${res.trimmed.length}`;
        if (res.deleted.length) message += `; deleted ${res.deleted.length}`;
        onSaved(res.op_id ? { message, opId: res.op_id } : undefined);
        onClose();
        return;
      }

      // EDIT mode (existing behavior below)
      // Per-field "did the user change this?" tests against the clicked item.
      const audienceChanged = audience !== item.audience;
      const actionChanged = action !== item.action;
      const timeChanged = !!(newMatchTimeIso && newMatchTimeIso !== item.starts_at);
      const durationChanged =
        !!duration &&
        duration !==
          (() => {
            const start = new Date(item.starts_at);
            const end = new Date(item.ends_at);
            const ms = end.getTime() - start.getTime();
            const h = Math.floor(ms / 3_600_000);
            const m = Math.floor((ms % 3_600_000) / 60_000);
            if (h > 0) return `PT${h}H${m > 0 ? m + "M" : ""}`;
            if (m > 0) return `PT${m}M`;
            return "PT1H";
          })();

      // The slot = all audience variants sharing (channel, action, starts_at).
      // Time/action/duration changes propagate across the slot; audience changes
      // only apply to the variant the user opened.
      const slotSiblings = (siblings && siblings.length ? siblings : [item]);
      const others = slotSiblings.filter((s) => s.audience !== item.audience);

      // 1) Update the clicked variant with everything the user changed.
      await updateEpgItem(item.channel, item.audience, item.action, item.starts_at, {
        new_action: actionChanged ? action : undefined,
        new_audience: audienceChanged ? audience : undefined,
        new_match_time: timeChanged ? newMatchTimeIso : undefined,
        new_duration: durationChanged ? duration : undefined,
      });

      // 2) Propagate slot-wide changes (time/action/duration) to other variants.
      const slotWide =
        (timeChanged || actionChanged || durationChanged) && others.length > 0
          ? {
              new_action: actionChanged ? action : undefined,
              new_match_time: timeChanged ? newMatchTimeIso : undefined,
              new_duration: durationChanged ? duration : undefined,
            }
          : null;

      if (slotWide) {
        const results = await Promise.allSettled(
          others.map((s) =>
            updateEpgItem(s.channel, s.audience, s.action, s.starts_at, slotWide)
          )
        );
        const failed = results.filter((r) => r.status === "rejected");
        if (failed.length) {
          console.warn(
            `[EpgEditor] partial save: ${failed.length}/${others.length} sibling update(s) failed`,
            failed
          );
        }
      }

      onSaved();
      onClose();
    } catch (err: any) {
      console.error("Save failed:", err);
      setError(err?.message || String(err));
    } finally {
      setSaving(false);
    }
  };

  const slotSiblings = (siblings && siblings.length ? siblings : [item]);
  const otherCount = slotSiblings.filter((s) => s.audience !== item.audience).length;

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <h3 className="grad-text" style={{ marginTop: 0 }}>{mode === "create" ? "Add MediaPoint" : "Edit MediaPoint"}</h3>
        <div style={{ display: "grid", gap: 12 }}>
          <label>
            <div>Channel</div>
            <input disabled value={item.channel} className="field" />
          </label>
          {mode === "edit" && (
            <label>
              <div>Audience</div>
              <input
                value={audience}
                onChange={(e) => setAudience(e.target.value)}
                className="field"
              />
            </label>
          )}
          {mode === "create" && (
            <div style={{ fontSize: 12, opacity: 0.75 }}>
              This slot will be created for every audience on {item.channel}; any
              overlapping mediapoints are trimmed.
            </div>
          )}
          <label>
            <div>Action / Content</div>
            <input
              value={action}
              onChange={(e) => setAction(e.target.value)}
              className="field"
            />
          </label>
          <label>
            <div>Start</div>
            <input
              type="datetime-local"
              value={startLocal}
              onChange={(e) => setStartLocal(e.target.value)}
              className="field"
            />
          </label>
          <label>
            <div>Duration (ISO 8601)</div>
            <input
              placeholder="PT1H, PT90M, PT2H30M"
              value={duration}
              onChange={(e) => setDuration(e.target.value)}
              className="field"
            />
          </label>
          {mode === "edit" && otherCount > 0 && (
            <div style={{ fontSize: 12, opacity: 0.75 }}>
              Time / action / duration changes will also apply to {otherCount} other audience
              variant{otherCount === 1 ? "" : "s"} in this slot.
            </div>
          )}
          {error && (
            <div style={{ color: "#ef4444", fontSize: 13 }}>Save failed: {error}</div>
          )}
        </div>

        <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", marginTop: 16 }}>
          <button className="btn ghost" onClick={onClose} disabled={saving}>Cancel</button>
          <button className="btn primary" onClick={onSave} disabled={saving}>
            {saving ? "Saving…" : "Save"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default EpgEditor;
